/**
 * Frontend mirror of role/persona constants (DOS-213).
 *
 * Kept in sync with server/capabilities/constants.js. Duplicated rather than
 * fetched at boot so labels are available synchronously for UI rendering.
 * If you change one side, update the other.
 */

// Display labels for platform roles. `org_admin` renders as "Group Admin" in UI.
export const ROLE_LABELS = {
  superadmin:     'Superadmin',
  org_admin:      'Group Admin',      // UI display only — stored as org_admin
  group_admin:    'Group Admin',      // alias
  location_admin: 'Location Admin',
  manager:        'Manager',
  user:           'User',
};

// Roles available for admin assignment in the Invite/Manage Users UI.
// superadmin intentionally omitted — only superadmins can grant superadmin.
export const ASSIGNABLE_ROLES = ['user', 'manager', 'location_admin', 'org_admin'];

// 12 personas with display labels + short descriptions.
export const PERSONAS = [
  { name: 'dealer_principal',      label: 'Dealer Principal',         hint: 'Owner / CEO. Strategic + executive skills, all financials.' },
  { name: 'general_manager',       label: 'General Manager',          hint: 'Daily operational lead. Broad skill access across departments.' },
  { name: 'fixed_ops_director',    label: 'Fixed Operations Director', hint: 'Service + parts + workforce skills, location data.' },
  { name: 'controller',            label: 'Controller / CFO',         hint: 'Financial skills + all-location financial data.' },
  { name: 'parts_director',        label: 'Parts Director',           hint: 'Parts inventory, GP, obsolescence skills.' },
  { name: 'sales_director',        label: 'Sales Director',           hint: 'Sales pipeline, truck deals, F&I.' },
  { name: 'fleet_account_manager', label: 'Fleet Account Manager',    hint: 'Fleet customer review + retention skills.' },
  { name: 'regional_ops_director', label: 'Regional Ops Director',    hint: 'Cross-location executive + financial + sales.' },
  { name: 'hr_workforce_manager',  label: 'HR / Workforce Manager',   hint: 'Staffing, tech productivity, OEM cert pipeline.' },
  { name: 'oem_program_rep',       label: 'OEM Program Manager',      hint: 'External stakeholder view — location financials only.' },
  { name: 'fleet_customer',        label: 'Fleet Customer',           hint: 'External fleet manager — own fleet account only.' },
  { name: 'service_technician',    label: 'Service Technician',       hint: 'Internal tech — own performance only.' },
];

export const PERSONA_LABELS = Object.fromEntries(PERSONAS.map(p => [p.name, p.label]));
export const PERSONA_HINTS = Object.fromEntries(PERSONAS.map(p => [p.name, p.hint]));

/**
 * Get a human-readable label for a role. Falls back to the raw name if unknown.
 */
export function roleLabel(role) {
  return ROLE_LABELS[role] || role || '';
}

/**
 * Get a human-readable label for a persona. Returns '' for null/undefined.
 */
export function personaLabel(persona) {
  if (!persona) return '';
  return PERSONA_LABELS[persona] || persona;
}

/**
 * Reverse mapping: capability name → array of persona names that have that capability.
 * Kept in sync with server/capabilities/constants.js PERSONA_CAPABILITIES matrix.
 * Used by AdminSkillsPage to display "Available to personas: ..." per skill.
 */
export const CAPABILITY_TO_PERSONAS = {
  run_skill_executive:     ['dealer_principal', 'general_manager', 'controller', 'regional_ops_director'],
  run_skill_financial:     ['dealer_principal', 'general_manager', 'controller', 'regional_ops_director'],
  run_skill_operational:   ['dealer_principal', 'general_manager', 'fixed_ops_director', 'parts_director', 'regional_ops_director'],
  run_skill_parts:         ['general_manager', 'fixed_ops_director', 'parts_director'],
  run_skill_sales:         ['general_manager', 'sales_director', 'regional_ops_director'],
  run_skill_fleet:         ['general_manager', 'fixed_ops_director', 'fleet_account_manager', 'regional_ops_director', 'fleet_customer'],
  run_skill_workforce:     ['dealer_principal', 'general_manager', 'fixed_ops_director', 'hr_workforce_manager'],
};

/**
 * For a given skill capability, return a short human-readable list of persona labels.
 * Returns 'All users' when no capability is required.
 */
export function personasForCapability(capability) {
  if (!capability) return ['All users'];
  const names = CAPABILITY_TO_PERSONAS[capability] || [];
  return names.map(n => PERSONA_LABELS[n] || n);
}
